import { FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import { ExpenseScenarioForm } from './scenario-form';
import { areaGeometriesAtom, fetchScenarioLoadableAtom } from './atoms';
import { DEFAULT_FORM_VALUES, mapResponseToFormData } from './utils';

/**
 * Page entry point for both create and edit modes.
 * Determines mode from URL params — if :id is present, it's edit mode and the
 * scenario is loaded via GET /expense_scenarios/:id before rendering the form.
 * @return {JSX.Element} The scenario form page
 */
export const ExpenseScenarioFormPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [fetchState, triggerFetch] = useAtom(fetchScenarioLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);

  useEffect(() => {
    if (id) triggerFetch(Number(id));
  }, [id, triggerFetch]);

  const mapped = useMemo(() => {
    if (!id || fetchState.state !== 'hasData' || !fetchState.data) return null;
    return mapResponseToFormData(fetchState.data);
  }, [id, fetchState]);

  useEffect(() => {
    if (mapped?.geometries) setGeometries(mapped.geometries);
  }, [mapped, setGeometries]);

  // The geometry cache is module-global and only ever written in edit mode, so
  // without this reset a fresh scenario inherits the previously-viewed one's
  // polygons. Create-mode area ids start at 1 and can collide with leftover
  // server-PK keys, drawing a stale polygon onto a brand new area.
  useEffect(() => {
    if (!id) setGeometries({});
  }, [id, setGeometries]);

  if (!id) {
    return (
      <ExpenseScenarioForm initialValues={DEFAULT_FORM_VALUES} mode="create" />
    );
  }

  if (fetchState.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        title="Failed to load scenario"
        hideCloseButton
        lowContrast
      />
    );
  }

  if (fetchState.state === 'loading' || !mapped) {
    return <InlineLoading description="Loading scenario..." />;
  }

  return (
    <ExpenseScenarioForm
      initialValues={mapped.formData}
      mode="edit"
      scenarioId={id}
    />
  );
};
